import type { ErrorEvent } from '@sentry/nextjs';

// Guards for the event shapes `captureConsoleIntegration` produces. Consumed
// by `beforeSend` in `sentry.client.config.ts`.

/**
 * Keys a structured logger uses for the human-readable line. `msg` is pino's;
 * `message` covers the other common shape.
 */
const STRUCTURED_LOG_MESSAGE_KEYS = ['msg', 'message'] as const;

/**
 * Cap on a recovered message. `Error.message` is unbounded and the recovered
 * text is arbitrary upstream log content, so bound what we write back onto the
 * event rather than letting one pathological log line define an issue title.
 */
const MAX_RECOVERED_MESSAGE_LENGTH = 500;

function extractStructuredLogMessage(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  for (const key of STRUCTURED_LOG_MESSAGE_KEYS) {
    const candidate = (value as Record<string, unknown>)[key];
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }
  return null;
}

/**
 * Recover a readable message from a console capture whose object arguments
 * were String()-cast to `[object Object]`.
 *
 * WHY this exists (BALLEE-GZ). `captureConsoleIntegration` joins console
 * arguments with `String()`, so a single object argument becomes the literal
 * `[object Object]` in BOTH `event.message` and `hint.originalException`
 * (@sentry/core 10.70.0: `safeJoin` → `stringifyValue` → `[object ${name}]`).
 * Our browser logger is pino configured with `browser: { asObject: true }`
 * (`packages/logger/src/impl/pino.ts`), so every client-side `logger.error()`
 * is literally `console.error({ level, msg, time })` — one object argument.
 * The diagnostic text then survives ONLY under `event.extra.arguments`.
 *
 * The cost is not cosmetic. Every distinct object-argument `console.error`
 * collapses into ONE Sentry fingerprint, and no message-matching filter can
 * see the real text — neither `IGNORED_ERROR_PATTERNS` (client-ignore-list.ts) nor Sentry's
 * ORG-LEVEL inbound filters. That is how a Turbopack chunk-load error, a class
 * already filtered org-wide by its string form (`chunk-load-error`, and
 * separately auto-reloaded by `apps/web/components/chunk-error-handler.tsx`),
 * still leaked through on /:handle as an unfiltered `[object Object]` issue.
 *
 * Deliberately conservative: it returns null unless an OBJECT argument
 * actually yielded a message. Without that guard `console.error('Prefixed:',
 * {code: 7})` would be rewritten from `Prefixed: [object Object]` to the
 * strictly less informative `Prefixed:`. Never widening the message, only
 * recovering text that would otherwise be unreachable.
 *
 * Recovery alone is not enough for the chunk-load class. The first version of
 * this note relied on Sentry's org-level `chunk-load-error` inbound filter to
 * drop the recovered text; BALLEE-HC / BALLEE-H7 falsified that. The filter
 * keys on webpack's `Loading chunk N failed` wording and the `ChunkLoadError`
 * exception type, and a Turbopack console capture carries neither. The
 * Turbopack sentence is therefore in `IGNORED_ERROR_PATTERNS` (client-ignore-list.ts), where the
 * BAL-856 drop telemetry records each drop under a stable pattern slug.
 */
export function recoverConsoleObjectMessage(event: ErrorEvent): string | null {
  const message = event.message;
  // `[object ` also covers `[object Array]`, `[object Null]`, etc.
  if (typeof message !== 'string' || !message.includes('[object ')) {
    return null;
  }

  const args = (event.extra as { arguments?: unknown } | undefined)?.arguments;
  if (!Array.isArray(args)) return null;

  const parts: string[] = [];
  let recoveredFromObject = false;

  for (const arg of args) {
    if (typeof arg === 'string') {
      if (arg.trim() !== '') parts.push(arg);
      continue;
    }
    const extracted = extractStructuredLogMessage(arg);
    if (extracted) {
      parts.push(extracted);
      recoveredFromObject = true;
    }
  }

  if (!recoveredFromObject) return null;

  const joined = parts.join(' ');
  if (joined === message) return null;

  return joined.length > MAX_RECOVERED_MESSAGE_LENGTH
    ? joined.slice(0, MAX_RECOVERED_MESSAGE_LENGTH)
    : joined;
}

/**
 * Next.js's own router sentence when a background RSC prefetch fails
 * (BALLEE-HF / BAL-1006).
 *
 * `next/dist/client/components/router-reducer/fetch-server-response.js` logs
 * it from inside its own `catch`, guarded by `!isPageUnloading`:
 *
 *   console.error(`Failed to fetch RSC payload for ${originalUrl}. ` +
 *                 `Falling back to browser navigation.`, err)
 *
 * and then does exactly what it says — a full browser navigation. That is the
 * router's designed resilience path, not an app defect: the user gets the page.
 *
 * WHY this is not an `IGNORED_ERROR_PATTERNS` entry, which is where every other
 * benign-console class lives. Two independent reasons, both verified rather
 * than assumed:
 *
 * 1. It would never match. `captureConsoleIntegration` does
 *    `args.find(arg => arg instanceof Error)` and, when one is present, calls
 *    `captureException(error)` — NOT `captureMessage` (@sentry/core 10.70.0,
 *    `integrations/captureconsole.js`). So the event is the second argument,
 *    a bare `TypeError: Failed to fetch`, and the sentence above survives ONLY
 *    under `event.extra.arguments`. Confirmed on the production event
 *    (issue 7687110684): `exception.values[0].value === 'Failed to fetch'`,
 *    `mechanism.type === 'auto.core.capture_console'`, and `extra.arguments[0]`
 *    carrying the full sentence. Neither string the ignore list is tested
 *    against contains "RSC payload", so a pattern there is dead code.
 * 2. It would be forbidden anyway. The `critical-rules-supabase-outage-
 *    resilience` clause bans `failed to fetch` from either config's
 *    `IGNORED_ERROR_PATTERNS`, and `apps/web/scripts/verify-resilience-
 *    architecture.sh` greps the array body for exactly that substring on every
 *    push. A pattern anchored on the longer sentence still contains it.
 *
 * Blast radius, bounded on three independent axes. Only the first is enforced
 * by the predicate; the other two are properties of the call site, and they are
 * what actually keeps this clear of the outage signals the clause protects.
 *
 * a. The predicate. The event must carry the console-capture mechanism AND an
 *    argument matching the FULL Next.js sentence. An uncaught
 *    `TypeError: Failed to fetch`, and app code that merely logs those words,
 *    have neither and are still reported.
 * b. A 5xx cannot produce this log at all. Verified in next@16.3.0,
 *    `fetch-server-response.js`: `fetch` RESOLVES for an error status, and the
 *    next branch — `if (!isFlightResponse || !res.ok || !res.body)` — returns
 *    `doMpaNavigation(...)` instead of throwing, so the `catch` never runs and
 *    nothing is logged. The outage class this clause protects arrives exactly
 *    that way: a Cloudflare 522/523/524 or any origin 5xx, HTML body, resolved
 *    response, non-RSC content-type. It never reaches this filter. The `catch`
 *    fires only when `fetch` itself REJECTS — DNS, TLS, connection reset,
 *    offline, CORS — or the Flight stream fails to decode.
 * c. An unreachable origin has no delivery path anyway. `tunnel` in sentry.client.config.ts is
 *    '/api/monitoring/sentry' — same-origin — so an event raised while our own
 *    origin is unreachable at the network layer cannot be sent with or without
 *    this filter.
 *
 * Do NOT restate (a) as the whole guarantee, which an earlier draft of this
 * comment did. An origin-unreachable PREFETCH does satisfy both halves of the
 * predicate and is dropped here. What makes that safe is (b) and (c).
 *
 * outage-string-ok: anchored on Next.js's full router sentence, which a 5xx
 * cannot produce (b), and on the console-capture mechanism (a). Checked by
 * `apps/web/app/__tests__/unit/sentry-outage-string-scan.test.ts` (BAL-1009).
 */
export const RSC_PREFETCH_FALLBACK_MESSAGE =
  /^failed to fetch rsc payload for .+\. falling back to browser navigation\.$/i;

/**
 * The Sentry mechanism `captureConsoleIntegration` stamps on every event it
 * creates (`addExceptionMechanism(event, { type: 'auto.core.capture_console' })`).
 */
const CONSOLE_CAPTURE_MECHANISM = 'auto.core.capture_console';

/**
 * URL-free stand-in reported to the drop telemetry instead of the real console
 * argument.
 *
 * The real sentence embeds the prefetch target — on the production event that
 * was `https://ballee.co/home/artist/profile/edit?section=physicalDimensions`.
 * `buildDropInfo` redacts `messagePrefix` by PATTERN (emails, uuids, jwts, hex
 * runs), so a short lowercase path token — a `/m/<token>` passwordless-login
 * credential is the documented example — would pass through unredacted. The
 * class is fully identified without the URL, and `route` already carries a
 * normalized, allowlisted path, so the URL buys nothing and risks a credential.
 *
 * outage-string-ok: a value REPORTED to drop telemetry, never matched against
 * an event, so it cannot silence anything (BAL-1009).
 */
export const RSC_PREFETCH_FALLBACK_DROP_VALUE =
  'Failed to fetch RSC payload. Falling back to browser navigation.';

/**
 * True when this event is Next.js's RSC-prefetch fallback log, captured by
 * `captureConsoleIntegration` as the underlying fetch error.
 */
export function isRscPrefetchFallbackConsoleCapture(
  event: ErrorEvent,
): boolean {
  if (
    event.exception?.values?.[0]?.mechanism?.type !== CONSOLE_CAPTURE_MECHANISM
  ) {
    return false;
  }

  const args = (event.extra as { arguments?: unknown } | undefined)?.arguments;
  if (!Array.isArray(args)) return false;

  // Argument 0 ONLY, never a scan of the whole array. Next.js always logs the
  // sentence first (`console.error(<sentence>, err)`), so narrowing costs
  // nothing and closes a widening: with a scan, ANY call site that passes
  // attacker-influenced text as a later console argument alongside a real
  // Error would have its event dropped.
  //
  // Deliberately NOT justified by a census of today's call sites. An earlier
  // draft of this comment carried one, and it was both stale on re-count and
  // beside the point: a census is an audit that has to be redone on every new
  // `console.error`, and nothing makes anyone redo it. The guarantee has to
  // come from the predicate. Reading one argument also bounds the regex scan
  // to one string.
  const [first] = args;
  return typeof first === 'string' && RSC_PREFETCH_FALLBACK_MESSAGE.test(first);
}
